import { useCallback } from "react";
import Badge from "@mui/material/Badge";
import Box from "@mui/material/Box";
import IconButton from "@mui/material/IconButton";
import TextField from "@mui/material/TextField";
import FilterListIcon from "@mui/icons-material/FilterList";
import SearchIcon from "@mui/icons-material/Search";
import ClearIcon from "@mui/icons-material/Clear";
import InputAdornment from "@mui/material/InputAdornment";

interface StickySearchBarProps {
  value: string;
  onChange: (value: string) => void;
  onSubmit?: () => void;
  /**
   * Called when the clear (×) button is pressed, after the value is reset to "".
   * Falls back to `onSubmit` when omitted. Prefer this over a render-time
   * `onSubmit` switch: `onSubmit` closures captured while the field still had
   * text would re-apply the old search instead of clearing it.
   */
  onClear?: () => void;
  onFilterClick: () => void;
  filterBadgeCount?: number;
  placeholder?: string;
}

export default function StickySearchBar({
  value,
  onChange,
  onSubmit,
  onClear,
  onFilterClick,
  filterBadgeCount = 0,
  placeholder = "Search...",
}: StickySearchBarProps) {
  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === "Enter") {
        onSubmit?.();
      }
    },
    [onSubmit],
  );

  const handleClear = useCallback(() => {
    onChange("");
    if (onClear) onClear();
    else onSubmit?.();
  }, [onChange, onClear, onSubmit]);

  return (
    <Box
      sx={{
        position: "sticky",
        top: 0,
        zIndex: 10,
        backgroundColor: "#fff",
        padding: "8px 16px",
        borderBottom: "1px solid #ccc",
        display: "flex",
        alignItems: "center",
        gap: "8px",
      }}
    >
      <TextField
        value={value}
        onChange={(e) => onChange(e.target.value)}
        onKeyDown={handleKeyDown}
        placeholder={placeholder}
        size="small"
        sx={{ flex: 1 }}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <SearchIcon sx={{ fontSize: 20, color: "#666" }} />
            </InputAdornment>
          ),
          endAdornment: value ? (
            <InputAdornment position="end">
              <IconButton onClick={handleClear} size="small" aria-label="Clear search">
                <ClearIcon sx={{ fontSize: 18 }} />
              </IconButton>
            </InputAdornment>
          ) : null,
        }}
      />
      <IconButton
        onClick={onFilterClick}
        aria-label="Open filters"
        sx={{ width: 44, height: 44 }}
      >
        <Badge badgeContent={filterBadgeCount} color="primary">
          <FilterListIcon />
        </Badge>
      </IconButton>
    </Box>
  );
}
